/* ── geocoding ───────────────────────────────────────────────
 * Everything that talks to Google, normalised into one shape.
 *
 * The awkward part this module exists to hide: MapPicker needs two
 * different Google APIs that disagree about their own data model.
 *
 *   · Places API (New) — used for autocomplete. Returns
 *     `addressComponents` with `longText` / `shortText`.
 *   · Geocoder (classic) — used for reverse geocoding when the user
 *     drags the map. Returns `address_components` with `long_name` /
 *     `short_name`.
 *
 * Same concept, two casings. `readComponents` below accepts either,
 * so the rest of the component never has to care which call produced
 * a location.
 * ──────────────────────────────────────────────────────────── */

import { loadMapsLibrary } from './googleMapsLoader';

/* ── Address component extraction ─────────────────────────── */

// Ordered by preference — first type present wins.
const COMPONENT_RULES = {
  streetNumber: ['street_number'],
  route: ['route'],
  landmark: [
    'point_of_interest',
    'premise',
    'neighborhood',
    'sublocality_level_1',
    'sublocality',
  ],
  city: [
    'locality',
    'postal_town',
    'administrative_area_level_3',
    'administrative_area_level_2',
  ],
  state: ['administrative_area_level_1'],
  country: ['country'],
  postalCode: ['postal_code'],
};

/** Reads a component list in either Places-New or Geocoder casing. */
const readComponents = (components) => {
  const list = (components || []).map((c) => ({
    long: c.longText ?? c.long_name ?? '',
    short: c.shortText ?? c.short_name ?? '',
    types: c.types || [],
  }));

  const pick = (wanted) => {
    for (const type of wanted) {
      const hit = list.find((c) => c.types.includes(type));
      if (hit) return hit;
    }
    return null;
  };

  const out = {};
  Object.entries(COMPONENT_RULES).forEach(([key, types]) => {
    out[key] = pick(types)?.long || '';
  });
  out.countryCode = pick(COMPONENT_RULES.country)?.short || '';
  out.stateCode = pick(COMPONENT_RULES.state)?.short || '';
  return out;
};

/**
 * Build the one shape MapPicker and its consumers speak.
 * `address` is the full formatted string; the rest are the structured
 * fields the store form persists separately.
 */
const toLocation = ({ lat, lng, formatted, components, placeId, viewport }) => {
  const parts = readComponents(components);
  const streetLine = [parts.streetNumber, parts.route].filter(Boolean).join(' ');

  return {
    lat: Number(Number(lat).toFixed(6)),
    lng: Number(Number(lng).toFixed(6)),
    address: formatted || '',
    // Prefer a real street line; fall back to the POI/neighbourhood name
    // so the confirmation card never shows an empty primary line.
    primaryLine: streetLine || parts.landmark || formatted || '',
    landmark: parts.landmark || '',
    city: parts.city || '',
    state: parts.state || '',
    country: parts.country || '',
    postalCode: parts.postalCode || '',
    countryCode: parts.countryCode || '',
    placeId: placeId || '',
    viewport: viewport || null,
  };
};

/* ── Zoom discipline ──────────────────────────────────────── */

// Only consulted when a result carries no viewport. Fitting the real
// viewport is always more accurate than guessing a zoom level.
const ZOOM_BY_TYPE = [
  [['subpremise', 'premise', 'street_address', 'point_of_interest'], 18],
  [['route', 'intersection'], 17],
  [['neighborhood', 'sublocality', 'sublocality_level_1'], 15],
  [['postal_code'], 14],
  [['locality', 'postal_town'], 12],
  [['administrative_area_level_2'], 10],
  [['administrative_area_level_1'], 8],
  [['country'], 5],
];

export const zoomForTypes = (types, fallback = 17) => {
  const list = types || [];
  for (const [candidates, zoom] of ZOOM_BY_TYPE) {
    if (candidates.some((t) => list.includes(t))) return zoom;
  }
  return fallback;
};

/* ── Request sequencing ───────────────────────────────────── */

/**
 * Guards against the out-of-order-response race. Two quick map drags
 * fire two reverse geocodes; if the first resolves last, the *older*
 * address wins and silently overwrites the newer one. Every call gets
 * a monotonic ticket and stale tickets are dropped on resolve.
 */
export const createSequencer = () => {
  let latest = 0;
  return async (task) => {
    const ticket = ++latest;
    const result = await task();
    if (ticket !== latest) return undefined; // superseded — discard
    return result;
  };
};

/* ── Autocomplete (Places API New) ────────────────────────── */

export const createSessionToken = async () => {
  const { AutocompleteSessionToken } = await loadMapsLibrary('places');
  return new AutocompleteSessionToken();
};

/**
 * @param {object}  opts
 * @param {string}  opts.input          the query
 * @param {object=} opts.sessionToken   groups keystrokes+selection into one billed session
 * @param {object=} opts.locationBias   LatLngBounds — soft preference, not a filter
 * @param {string=} opts.region         CLDR region code, biases interpretation
 * @param {string[]=} opts.countries    restrict to these country codes
 */
export const fetchSuggestions = async ({
  input,
  sessionToken,
  locationBias,
  region,
  countries,
}) => {
  const { AutocompleteSuggestion } = await loadMapsLibrary('places');

  const request = { input };
  if (sessionToken) request.sessionToken = sessionToken;
  // Bias, never restrict: restriction hard-excludes results outside the
  // box, which strands a user who pans away and then searches a city
  // they can no longer see.
  if (locationBias) request.locationBias = locationBias;
  if (region) request.region = region;
  if (countries?.length) request.includedRegionCodes = countries;

  const { suggestions } =
    await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

  return (suggestions || [])
    .filter((s) => s.placePrediction)
    .map((s) => {
      const p = s.placePrediction;
      return {
        placeId: p.placeId,
        text: p.text?.toString?.() ?? String(p.text ?? ''),
        mainText: p.mainText?.toString?.() ?? '',
        secondaryText: p.secondaryText?.toString?.() ?? '',
        types: p.types || [],
        toPlace: () => p.toPlace(),
      };
    });
};

/** Resolve a chosen suggestion into a full location. */
export const resolveSuggestion = async (suggestion) => {
  const place = suggestion.toPlace();
  await place.fetchFields({
    fields: [
      'id',
      'displayName',
      'formattedAddress',
      'location',
      'addressComponents',
      'viewport',
      'types',
    ],
  });

  const lat = place.location?.lat?.() ?? place.location?.lat;
  const lng = place.location?.lng?.() ?? place.location?.lng;

  const location = toLocation({
    lat,
    lng,
    formatted: place.formattedAddress || suggestion.text,
    components: place.addressComponents,
    placeId: place.id || suggestion.placeId,
    viewport: place.viewport || null,
  });

  // A named POI ("Blue Bottle Coffee") is a better primary line than its
  // street number, and it is what the user actually searched for.
  const displayName = place.displayName?.toString?.() ?? place.displayName;
  if (displayName && displayName !== location.primaryLine) {
    location.primaryLine = displayName;
  }
  location.types = place.types || suggestion.types || [];
  return location;
};

/* ── Reverse geocoding (classic Geocoder) ─────────────────── */

let geocoderInstance = null;

const getGeocoder = async () => {
  if (geocoderInstance) return geocoderInstance;
  const { Geocoder } = await loadMapsLibrary('geocoding');
  geocoderInstance = new Geocoder();
  return geocoderInstance;
};

/** Map coordinates → a location. Returns null when nothing matches. */
export const reverseGeocode = async ({ lat, lng, language }) => {
  const geocoder = await getGeocoder();
  const request = { location: { lat, lng } };
  if (language) request.language = language;

  const { results } = await geocoder.geocode(request);
  if (!results?.length) return null;

  // results[0] is the most specific match Google has for the point.
  const best = results[0];
  const location = toLocation({
    lat,
    lng,
    formatted: best.formatted_address,
    components: best.address_components,
    placeId: best.place_id,
    viewport: best.geometry?.viewport || null,
  });
  location.types = best.types || [];

  // Google flags coarse matches. Surfacing this is what lets the UI say
  // "this is approximate — drag to adjust" instead of implying a
  // rooftop-accurate pin it does not actually have.
  location.isApproximate =
    best.geometry?.location_type === 'APPROXIMATE' ||
    best.geometry?.location_type === 'GEOMETRIC_CENTER';

  return location;
};

export { toLocation, readComponents };
